(ns storrito.connect3.login-panel
  (:require [app.client-log :as client-log]
            [storrito.ui.button :as button]
            [storrito.connect3.common :as common]
            [storrito.util.country-code :as country-code]
            [clojure.string :as str]
            [ui.totp.generate :as totp-gen]
            [app.totp.validation :as validation]))

;; Validation functions
(defn valid-instagram-username? [username]
  ;; validated as it will be sent (see `common/login-data`)
  (when username
    (let [username (str/trim username)]
      (and (>= (count username) 3)
           (<= (count username) 30)
           (re-matches #"^[a-zA-Z0-9._]+$" username)
           (not (re-matches #"^[0-9]+$" username)) ; Not just digits
           (not (re-matches #"^[0-9._]+$" username)) ; Not just digits, dots, and underscores
           (not (re-matches #"^[a-zA-Z0-9._]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$" username)) ; Not an email
           (not (re-matches #"^\+?[0-9\s\-\(\)]+$" username)))))) ; Not a phone number

(defn get-username-validation-error [username]
  (let [username (some-> username str/trim)]
    (cond
      (not username)
      nil

      ;; Check email first, so that e.g. a 40-character email address yields
      ;; "not an email address" instead of the confusing "must be 30 characters
      ;; or less" or "can only contain letters, numbers, dots, and underscores".
      (re-matches #"^[a-zA-Z0-9._]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$" username)
      "Please enter a username, not an email address"

      ;; Phone numbers with a "+" or separators, e.g. "+49 221 17889011". Digits
      ;; without separators fall through to "Username cannot be just numbers".
      (and (re-matches #"^\+?[0-9\s\-\(\)]+$" username)
           (re-find #"[+\s\-\(\)]" username))
      "Please enter a username, not a phone number"

      (< (count username) 3)
      "Username must be at least 3 characters long"

      (> (count username) 30)
      "Username must be 30 characters or less"

      (not (re-matches #"^[a-zA-Z0-9._]+$" username))
      "Username can only contain letters, numbers, dots, and underscores"

      (re-matches #"^[0-9]+$" username)
      "Username cannot be just numbers"

      (re-matches #"^[0-9._]+$" username)
      "Username must contain at least one letter"

      :else nil)))

;; Proxy validation functions
(defn valid-proxy-url? [proxy-url]
  (or (not proxy-url)
      (empty? proxy-url)
      (re-find #"^(https?|socks5)://([a-zA-Z0-9._%-]+(?::[a-zA-Z0-9._%-]+)?@)?[a-zA-Z0-9.-]+:[0-9]+$" proxy-url)))

(defn login-data-complete?
  "True when the form can be submitted: a valid username, a non-blank
   password and, if given, a valid proxy URL. An empty password used to
   pass (an empty string is truthy) and reached PHP, where _setUser()
   threw an InvalidArgumentException that ended the session."
  [state]
  (boolean (and (valid-instagram-username? (:username state))
                (not (str/blank? (:password state)))
                (valid-proxy-url? (:proxy state)))))

(defn get-proxy-validation-error [proxy-url]
  (cond
    (or (not proxy-url) (empty? proxy-url))
    nil

    (not (re-find #"^(https?|socks5)://" proxy-url))
    "Proxy URL must start with http://, https://, or socks5://"

    (not (re-find #":[0-9]+$" proxy-url))
    "Proxy URL must end with a port number (e.g., :8080)"

    (not (re-find #"^(https?|socks5)://([a-zA-Z0-9._%-]+(?::[a-zA-Z0-9._%-]+)?@)?[a-zA-Z0-9.-]+:[0-9]+$" proxy-url))
    "Invalid proxy URL format. Use: protocol://[username:password@]host:port"

    :else nil))

(defn trust-banner
  "Explains why Storrito asks for the Instagram password — the single
   scariest moment of onboarding. Shown expanded by default: collapsed to
   a quiet one-liner it was opened by 3 % of the sessions, while 52 % of
   the sessions that reached this form (247 of 479, 16.–23.09.2026, 151
   new signups) never submitted a password and left. Users who have read
   it can hide it. Mirrors the trust banner shown on the approval panel
   so both steps tell the same story."
  [state]
  (if (false? (:show-trust-info? state))
    [:button.w-full.text-center.text-xs.text-gray-500.hover:text-gray-700.mb-4.cursor-pointer
     {:type "button"
      :on {:click [[:store/assoc-in [:connect3 :show-trust-info?] true]]}}
     [:i.fas.fa-shield-alt.mr-1.text-gray-400]
     "Why do we need your Instagram login?"]
    (let [location (common/location-label state)]
      [:div.w-full.bg-gradient-to-r.from-blue-50.to-indigo-50.border.border-blue-200.rounded-xl.p-4.mb-4
       ;; Funnel signal: how many users hesitate at the password ask
       ;; badly enough to re-open the explanation after hiding it (it is
       ;; open by default, so the mount alone says nothing).
       (when (true? (:show-trust-info? state))
         {:replicant/on-mount (fn [_]
                                (client-log/log! :connect3/trust-info-opened))})
       [:div.flex.items-start.gap-3
        [:div.h-10.w-10.rounded-full.bg-white.border.border-blue-200.flex.items-center.justify-center.flex-shrink-0
         [:i.fas.fa-shield-alt.text-blue-600]]
        [:div.flex-1.text-left
         [:p.text-sm.font-semibold.text-gray-900.mb-1
          "Why we ask for your Instagram login"]
         [:p.text-sm.text-gray-700
          "Storrito connects to your account like Instagram on a second
           device (a "
          [:b (common/device-label state)]
          (if location
            (list " in " [:b location])
            " in your region")
          "), so it can post for you — even when your computer is off."]
         [:p.text-xs.text-gray-500.mt-1
          "Your login is encrypted and only used for this connection. You can
           disconnect the account in Storrito at any time."]
         [:p.text-xs.text-gray-500.mt-1
          "Instagram may ask you to confirm this login in their app — that's
           expected and only takes a moment."]]
        [:button.text-gray-400.hover:text-gray-600.flex-shrink-0.cursor-pointer
         {:type "button"
          :aria-label "Hide"
          :on {:click [[:store/assoc-in [:connect3 :show-trust-info?] false]]}}
         [:i.fas.fa-times]]]])))

(defn panel
  [state]
  (let [username (:username state)
        username-error (get-username-validation-error username)
        is-username-valid (valid-instagram-username? username)
        proxy (:proxy state)
        proxy-error (get-proxy-validation-error proxy)
        is-proxy-valid (valid-proxy-url? proxy)]
    [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-8.pb-8.max-w-md.w-full.mx-auto.flex.flex-col.items-center
     ;; Instagram image at the top
     [:img.w-40.mb-4
      {:src "/img/instagram-new-logo.png"
       :alt "Instagram login"}]
     (trust-banner state)
     ;; Form fields. A real <form> so password managers (1Password,
     ;; Bitwarden, browser built-ins) recognise the username/password pair
     ;; as a login form and offer to fill it, and so Enter submits. Without
     ;; it, passwords get copy-pasted by hand — a stray space in the paste
     ;; cost one customer twelve days of rejected logins (brain-73). The
     ;; browser's own validation is off: the fields validate as you type and
     ;; the Login button stays disabled until the form is complete.
     [:form.w-full
      {:noValidate true
       :on {:submit (cond-> [[:js/prevent-default]]
                      (and (not (:sending? state))
                           (login-data-complete? state))
                      (conj [:connect3/send! (common/login-data state)]))}}
      [:div.w-full.space-y-4
       [:div
        [:label.block.text-sm.font-medium.text-gray-700.mb-1 {:for "username"} "Username"]
        ;; The credential inputs are each wrapped in their own div so the input
        ;; is the only replicant-managed child of its parent. Password managers
        ;; (e.g. LastPass) insert their icon element as a sibling right after
        ;; the input; replicant addresses children by index, so a foreign
        ;; sibling shifts the index of everything after the input (like the
        ;; error message below) and makes replicant's DOM updates crash.
        [:div
         [:input#username.w-full.px-4.py-2.border.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.text-gray-800.bg-gray-50.transition-all.duration-200
          {:class (cond
                    username-error ["border-red-300" "focus:ring-red-500" "focus:border-red-500"]
                    is-username-valid ["border-green-300" "focus:ring-green-500" "focus:border-green-500"]
                    :else ["border-gray-300"])
           :required true
           :type "text"
           :minLength 3
           :name "username"
           :autoComplete "username"
           :placeholder "Username / Instagram handle"
           :value username
           ;; `:input` instead of `:change`: `:change` only fires on blur when
           ;; the value differs from the focus-time value. If the DOM value and
           ;; the store diverge (e.g. browser autofill writes into the field),
           ;; re-typing the same visible text never fires `:change`, so the user
           ;; can get stuck with a stale validation error they cannot clear.
           :on {:input [[:store/assoc-in [:connect3 :username] :event/target.value]]}}]]
        (when username-error
          [:div.text-red-600.text-xs.mt-1 username-error])]]

       [:div.mt-2.w-full
        [:label.block.text-sm.font-medium.text-gray-700.mb-1
         {:for "password"}
         "Password"]
        [:div.relative
         ;; Wrapper div for password-manager compatibility, see the username
         ;; input above. Also keeps the eye toggle button's sibling index
         ;; stable.
         [:div
          [:input#password.w-full.px-4.py-2.pr-12.border.border-gray-300.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.text-gray-800.bg-gray-50.transition-all.duration-200
           {:required true
            :placeholder "Password"
            :type (if (:show-password state) "text" "password")
            :minLength 5
            :name "password"
            :on {:input [[:store/assoc-in [:connect3 :password] :event/target.value]]}
            ;; "current-password", not "new-password": with "new-password"
            ;; browsers and password managers offer to *generate* a password
            ;; and refuse to fill a saved one, so users copy-paste the
            ;; Instagram password by hand (brain-73). The old worry — a
            ;; silently auto-filled Storrito password — does not apply:
            ;; Storrito's own login lives on authkit.storrito.com, no
            ;; password form exists on the tenant domain.
            :autoComplete "current-password"}]]
         [:button.absolute.inset-y-0.right-0.pr-3.flex.items-center
          {:type "button"
           :tabIndex -1
           :on {:click [[:store/assoc-in [:connect3 :show-password] (not (:show-password state))]]}
           :style {:cursor "pointer"}}
          (if (:show-password state)
            [:svg.size-6.text-gray-400.hover:text-gray-600
             {:xmlns "http://www.w3.org/2000/svg"
              :fill "none"
              :viewBox "0 0 24 24"
              :stroke-width "1.5"
              :stroke "currentColor"}
             [:path
              {:stroke-linecap "round"
               :stroke-linejoin "round"
               :d "M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"}]]
            [:svg.size-6.text-gray-400.hover:text-gray-600
             {:xmlns "http://www.w3.org/2000/svg"
              :fill "none"
              :viewBox "0 0 24 24"
              :stroke-width "1.5"
              :stroke "currentColor"}
             [:path
              {:stroke-linecap "round"
               :stroke-linejoin "round"
               :d "M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"}]
             [:path
              {:stroke-linecap "round"
               :stroke-linejoin "round"
               :d "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"}]]
             )]]
        (when (common/edge-whitespace? (:password state))
          [:p.text-xs.text-amber-700.mt-1
           "Your password starts or ends with a space. Instagram allows that,
           but if it isn't part of your password, remove it."])]

       ;; Advanced Options Section
       [:div.w-full.mt-8
        [:details.border.border-gray-200.rounded-lg.overflow-hidden.shadow-sm
         [:summary.px-4.py-3.cursor-pointer.text-sm.font-medium.text-gray-700.bg-gray-50.hover:bg-gray-100.transition-colors.duration-200.outline-none
          "Advanced Options"]
         [:div.p-4.bg-gray-50.space-y-4
          [:div
           [:label.block.text-sm.font-medium.text-gray-700.mb-2 "Proxy URL (Optional)"]
           [:input.w-full.px-3.py-2.border.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.bg-white.transition-all.duration-200
            {:class (cond
                      proxy-error ["border-red-300" "focus:ring-red-500" "focus:border-red-500"]
                      (and (seq proxy) is-proxy-valid) ["border-green-300" "focus:ring-green-500" "focus:border-green-500"]
                      :else ["border-gray-300"])
             :type "text"
             :placeholder "http://username:password@host:port"
             :value proxy
             :on {:input [[:store/assoc-in [:connect3 :proxy] :event/target.value]]}}]
           (when proxy-error
             [:div.text-red-600.text-xs.mt-1 proxy-error])]
          (let [raw-totp (or (:totp-secret state) "")
                secret (validation/clean-secret raw-totp)
                char-count (count secret)
                has-input? (pos? char-count)
                valid-chars? (or (empty? secret)
                                 (validation/valid-base32? secret))
                valid? (validation/valid-secret-format? secret)
                preview-otp (when valid? (totp-gen/generate-otp secret))
                ;; :ui/clock is ticked once per second by the :totp/refresh
                ;; interval (started when the preview box mounts, see below),
                ;; which re-renders the panel so the countdown stays live.
                preview-progress (when preview-otp
                                   (/ (totp-gen/seconds-remaining
                                        (or (:ui/clock state) (js/Date.now)))
                                      totp-gen/totp-period))]
            [:div
             [:label.block.text-sm.font-medium.text-gray-700.mb-2 "TOTP Secret Key (Optional)"]
             [:input.w-full.px-3.py-2.border.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.bg-white.transition-all.duration-200.font-mono
              {:class (cond
                        (and has-input? (not valid-chars?))
                        ["border-red-300" "focus:ring-red-500" "focus:border-red-500"]
                        valid?
                        ["border-green-300" "focus:ring-green-500" "focus:border-green-500"]
                        :else
                        ["border-gray-300" "focus:ring-blue-500" "focus:border-blue-500"])
               :type "text"
               :placeholder "32-character secret key for auto 2FA"
               :value raw-totp
               :on {:input [[:store/assoc-in [:connect3 :totp-secret] :event/target.value]]}}]
             (if preview-otp
               [:div.mt-2.rounded-md.border.border-green-200.bg-green-50.p-2.5
                ;; Starts the 1-second :ui/clock interval that drives the
                ;; countdown bar. It stops itself when the user leaves the
                ;; connect3 page (see the :totp/refresh action handler).
                ;; The :replicant/key is required for on-mount to fire: without
                ;; it, replicant reuses the hint div from the else-branch (both
                ;; are divs) and updates it in place, so the preview box never
                ;; actually mounts.
                {:replicant/key :totp-preview
                 :replicant/on-mount [[:totp/refresh]]}
                [:div.flex.items-center.justify-between
                 [:span.text-xs.font-medium.text-green-700 "Verification code"]
                 [:div.flex.items-center.gap-2
                  [:span.text-lg.font-mono.font-bold.tracking-widest.text-gray-900
                   (subs preview-otp 0 3) " " (subs preview-otp 3)]
                  [:button.text-green-600.hover:text-green-700
                   {:on {:click [[:ig-account-modal/copy-otp preview-otp]]}
                    :title "Copy code"
                    :type "button"}
                   [:i.fas.fa-copy.text-xs]]]]
                [:div.mt-1.5
                 [:div.h-1.w-full.bg-green-100.rounded-full.overflow-hidden
                  [:div.h-full.rounded-full.transition-all
                   {:class (if (< preview-progress 0.2) "bg-red-400" "bg-green-500")
                    :style {:width (str (* 100 preview-progress) "%")}}]]]
                [:div.text-xs.text-green-600.mt-1
                [:i.fas.fa-arrow-right.mr-1]
                "Verify this code in Instagram, then click "
                [:span.font-semibold "Confirm & Login"]]]
               [:div.text-xs.mt-1
                (cond
                  (and has-input? (not valid-chars?))
                  [:span.text-red-500 "Only base32 characters allowed (A-Z, 2-7)"]

                  (and has-input? (> char-count validation/secret-key-length))
                  [:span.text-red-500 (str "Too long (" char-count " chars, need exactly " validation/secret-key-length ")")]

                  has-input?
                  [:span.text-gray-400 (str char-count "/" validation/secret-key-length " characters")]

                  :else
                  [:span.text-gray-500 "If provided, Storrito will automatically handle 2FA for future reconnects."])])])
          [:div
           [:label.block.text-sm.font-medium.text-gray-700.mb-2 "Country"]
           [:select.w-full.px-3.py-2.border.border-gray-300.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.bg-white.transition-all.duration-200
            {:value (:country state "")
             :on {:change [[:store/assoc-in [:connect3 :country] :event/target.value]]}}
            [:option {:value ""} "Select a country"]
            [:optgroup {:label "Common Countries"}
             (doall (map (fn [[country-name code]]
                           [:option.font-semibold {:key (str code "-common") :value code :selected (= code (:country state))} country-name])
                         (sort country-code/common-country-name->code)))]
            [:optgroup {:label "All Countries"}
             (doall (map (fn [[country-name code]]
                           [:option {:key code :value code :selected (= code (:country state))} country-name])
                         (sort country-code/country-name->code)))]]]]]]
       (common/error-message state)

       [:div.flex.justify-end.w-full.mt-8
        (let [totp-secret (validation/clean-secret (or (:totp-secret state) ""))
              has-valid-totp? (validation/valid-secret-format? totp-secret)]
          (button/button-primary-lg
            {:class "w-full"
             :disabled (or (:sending? state)
                           (not (login-data-complete? state)))
             ;; Submits the <form> above: click and Enter take the same path.
             :type "submit"}
            (cond
              (:sending? state)
              "Starting login..."

              has-valid-totp?
              [:span [:i.fas.fa-check.mr-2] "Confirm & Login"]

              :else
              "Login")))]
       (when (:slow-start? state)
         [:p.text-xs.text-gray-500.mt-2.text-center.w-full
          "Finding a good connection to Instagram is taking longer than usual.
          Hang on — this can take up to a minute."])]

     [:div.text-center.mt-4
      [:a.text-xs.text-blue-600.dark:text-blue-500.hover:underline.cursor-pointer
       {:on {:click [[:store/assoc-in [:connect3 :email-connect?] true]
                     [:store/assoc-in [:connect3 :error-message] nil]]}}
       "Having troubles? Try an alternative connect method."]]
      ]
))